import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Box, Typography, Button, Card, Stack, List, ListItem,
  ListItemButton, ListItemContent, Chip, Link as JoyLink, CircularProgress,
} from '@mui/joy';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useFeedback } from '../feedback/Service';
import { parseGraphQLError } from '../../utils';
import { getProvider } from './providers';
import { getProviderQueries } from './providerQueryMap';
import useUser from '../auth/useUser';
import LargeLoader from '../LargeLoader';

const IMPORT_ATS_JOB = gql`
  mutation ImportATSJob($providerId: String!, $atsJobId: String!) {
    importATSJob(providerId: $providerId, atsJobId: $atsJobId) {
      _id
      title
    }
  }
`;

const ATSImportJob = () => {
  const navigate = useNavigate();
  const feedback = useFeedback();
  const { user, loading: userLoading } = useUser();
  const providerId = user?.client?.selectedATSProvider;
  const provider = providerId ? getProvider(providerId) : null;
  const pq = providerId ? getProviderQueries(providerId) : null;
  const [selectedJobId, setSelectedJobId] = useState(null);

  const { data: statusData, loading: statusLoading } = useQuery(pq?.GET_STATUS, {
    skip: !pq,
    fetchPolicy: 'network-only',
  });

  const { data: jobsData, loading: jobsLoading } = useQuery(pq?.LIST_JOBS, {
    skip: !pq,
    fetchPolicy: 'network-only',
  });

  // Guard: no provider → jobs
  useEffect(() => {
    if (!userLoading && !providerId) {
      navigate('/jobs');
    }
  }, [userLoading, providerId, navigate]);

  // Guard: not connected → connect-ats
  useEffect(() => {
    if (!statusLoading && statusData && pq) {
      const status = statusData[pq.statusField];
      if (!status?.enabled) {
        navigate('/onboarding/connect-ats');
      }
    }
  }, [statusData, statusLoading, pq, navigate]);

  const [importJob, { loading: importing }] = useMutation(IMPORT_ATS_JOB, {
    onCompleted: ({ importATSJob }) => {
      navigate(`/onboarding/job/${importATSJob._id}?next=import-candidates`);
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: 'error' });
    },
  });

  const handleImport = () => {
    if (!selectedJobId) return;
    importJob({ variables: { providerId, atsJobId: selectedJobId } });
  };

  if (userLoading || statusLoading) return <LargeLoader />;
  if (!provider || !pq) return null;

  const jobs = jobsData?.[pq.jobsField] ?? [];

  return (
    <Box
      sx={{
        py: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: 'md',
        mx: 'auto',
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Typography level="h4" gutterBottom>Import a job from your ATS</Typography>
        <Typography level="body-sm" color="neutral">
          Select a job &mdash; we&apos;ll import its details to set up your interview
        </Typography>
      </Box>

      <Card variant="outlined" sx={{ p: 0 }}>
        {jobsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size="md" />
          </Box>
        ) : jobs.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography level="body-sm" color="neutral">No open jobs found in {provider.name}.</Typography>
          </Box>
        ) : (
          <List sx={{ '--List-padding': '0px' }}>
            {jobs.map((job, i) => (
              <ListItem key={job.id} sx={{ borderBottom: i < jobs.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                <ListItemButton
                  selected={selectedJobId === job.id}
                  disabled={!!job.linkedCavalryJobId}
                  onClick={() => setSelectedJobId(job.id)}
                  sx={{ gap: 1 }}
                >
                  <ListItemContent>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography level="title-sm">{job.title}</Typography>
                      {job.linkedCavalryJobId ? (
                        <Chip size="sm" color="success" variant="soft">Imported</Chip>
                      ) : job.status && (
                        <Chip size="sm" color={job.status === 'open' ? 'success' : 'neutral'} variant="soft">
                          {job.status}
                        </Chip>
                      )}
                    </Stack>
                    <Typography level="body-xs" color="neutral">
                      {[job.departmentName, job[pq.locationField]].filter(Boolean).join(' · ')}
                    </Typography>
                  </ListItemContent>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Card>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="solid"
          color="primary"
          disabled={!selectedJobId || jobsLoading}
          loading={importing}
          onClick={handleImport}
        >
          Import selected job
        </Button>
        <JoyLink
          level="body-sm"
          color="neutral"
          component="button"
          onClick={() => navigate('/onboarding/job/new')}
        >
          Skip &mdash; I&apos;ll create a job manually
        </JoyLink>
      </Stack>
    </Box>
  );
};

export default ATSImportJob;
